import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { Home as HomeIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import { FumigationContractorList } from "features/admin/FumigationContractorList/FumigationContractorList";
import { PendingReview } from "features/admin/PendingReview/PendingReview";
import { DeletedFumigationCompanies } from "features/admin/DeletedFumigationCompanies/DeletedFumigationCompanies";
import { ATMLoader } from "components/atoms/ATMLoader/ATMLoader.component";
import useFumigationCompanyDetails, { useInvalidateFumigationCompanyDetails } from "hooks/useFumigationCompanyDetails";

const AdminPortalPage: React.FC = () => {
    const [contentView, setContentView] = useState<string>('fumigationInformation');
    const navigate = useNavigate();

    // Single shared fetch — all child components read from this same cache entry.
    const { data, isLoading, isError } = useFumigationCompanyDetails();
    const invalidate = useInvalidateFumigationCompanyDetails();

    const allRecords = data?.data ?? [];

    const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
        setContentView(newValue);
    };

    const handleHomeClick = () => {
        navigate('/');
    };

    const renderContent = () => {
        switch (contentView) {
            case 'fumigationInformation':
                return (
                    <FumigationContractorList
                        fumigationCompanyDetails={allRecords}
                        onRefresh={invalidate}
                    />
                );
            case 'pendingReview':
                return <PendingReview allRecords={allRecords} isLoading={isLoading} onRefresh={invalidate} />;
            case 'deletedFumigationCompanies':
                return <DeletedFumigationCompanies allRecords={allRecords} isLoading={isLoading} />;
            default:
                return null;
        }
    };

    return (
        <Box component="main">
            {/* Home Button */}
            <Box sx={{ mb: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<HomeIcon />}
                    onClick={handleHomeClick}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#001A70',
                        borderColor: '#001A70',
                        '&:hover': {
                            backgroundColor: '#001A70',
                            color: '#fff',
                            borderColor: '#001A70',
                        },
                        '&:focus': {
                            outline: '2px solid #000',
                            outlineOffset: '2px',
                        },
                    }}
                >
                    Home
                </Button>
            </Box>

            <Paper elevation={2} sx={{ borderRadius: '4px', overflow: 'hidden' }}>
                <Tabs
                    value={contentView}
                    onChange={handleTabChange}
                    sx={{
                        backgroundColor: '#f5f5f5',
                        borderBottom: '1px solid #e0e0e0',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            minHeight: '48px',
                            // Remove the focus-visible border/outline ring that appears on click
                            '&:focus-visible': {
                                outline: 'none',
                            },
                            '&.Mui-focusVisible': {
                                outline: 'none',
                                boxShadow: 'none',
                            },
                        },
                        '& .Mui-selected': { color: '#0033A0' },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#0033A0',
                            height: '3px',
                        },
                    }}
                >
                    <Tab label="Fumigation Information" value="fumigationInformation" />
                    <Tab label="Pending Review" value="pendingReview" />
                    <Tab label="Deleted Fumigation Companies" value="deletedFumigationCompanies" />
                </Tabs>

                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <ATMLoader active inline="centered">Loading Fumigation Company Details...</ATMLoader>
                    </Box>
                ) : isError ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" color="error.main">
                        Failed to load fumigation company data. Please refresh the page.
                    </Box>
                ) : (
                    renderContent()
                )}
            </Paper>
        </Box>
    );
};

export default AdminPortalPage;
